import { LimeCheckbox } from "@/Components/NextBase/LimeCheckbox";
import { LimeInfoTooltip } from "@/Components/NextBase/LimeInfoTooltip";
import type { GetAppointmentCancelPreflight } from "@/server-types";
import { Trans } from "@lingui/react/macro";
import type { UseFormReturnType } from "@mantine/form";

type CancelPreflight = GetAppointmentCancelPreflight["response"];

/**
 * Every decision the dialog can carry, so nothing survives from the appointment
 * cancelled before it — the modal stays mounted and its form is never reset.
 * Key the seeding effect on the preflight alone: re-seeding on every render would
 * undo every tick.
 */
export function subscriptionDecisionDefaults(options: CancelPreflight) {
  return {
    excludeFromSubscriptionUsage: false,
    issueMakeupCredit: options.wouldIssueCredit,
    reissueCredit: options.wouldReissueCredits,
    extendCredit: false,
  };
}

function hasSubscriptionDecisions(
  options: CancelPreflight | undefined,
): options is CancelPreflight {
  return (
    options != null &&
    (options.hasAllowanceCustomerAppointments ||
      options.hasCreditBackedCustomerAppointments)
  );
}

/** No decision to put to the admin means `client-policy`, not an invented override. */
export function toSubscriptionCancellationDecision(
  options: CancelPreflight | undefined,
  values: {
    excludeFromSubscriptionUsage: boolean;
    issueMakeupCredit: boolean;
    reissueCredit: boolean;
    extendCredit: boolean;
  },
) {
  if (!hasSubscriptionDecisions(options)) {
    return { mode: "client-policy" } as const;
  }

  return {
    mode: "staff-override",
    countsTowardUsage: !values.excludeFromSubscriptionUsage,
    issueMakeupCredit: values.issueMakeupCredit,
    reissueCredit: values.reissueCredit,
    extendCredit: values.extendCredit,
  } as const;
}

export function SubscriptionCancellationDecisions<
  T extends {
    excludeFromSubscriptionUsage: boolean;
    issueMakeupCredit: boolean;
    reissueCredit: boolean;
    extendCredit: boolean;
  },
>({
  options,
  form,
}: {
  options: CancelPreflight | undefined;
  form: UseFormReturnType<T>;
}) {
  if (!hasSubscriptionDecisions(options)) {
    return null;
  }

  return (
    <div className="rounded-medium bg-default-100 flex w-full flex-col gap-4 p-3">
      <p className="text-sm font-medium">
        <Trans>Paketi</Trans>
      </p>

      {options.hasAllowanceCustomerAppointments && (
        <div>
          <p className="text-default-700 text-xs">
            <Trans>
              Odpovedani termini se privzeto štejejo v porabo paketa.
            </Trans>
          </p>

          <div className="mt-2 flex flex-col gap-1">
            <LimeCheckbox
              size="sm"
              {...form.getInputProps("excludeFromSubscriptionUsage", {
                type: "checkbox",
              })}
            >
              <Trans>Termin se ne šteje v redno porabo paketa</Trans>
            </LimeCheckbox>
            <LimeCheckbox
              size="sm"
              {...form.getInputProps("issueMakeupCredit", { type: "checkbox" })}
            >
              <Trans>Dodeli nadomestni termin</Trans>
            </LimeCheckbox>
          </div>

          {form.values.excludeFromSubscriptionUsage &&
            form.values.issueMakeupCredit && (
              <p className="text-warning-600 mt-1 text-xs">
                <Trans>
                  Stranki bo povrnjenih preveč terminov - redni obisk ji ostane
                  in dobi še nadomestni termin.
                </Trans>
              </p>
            )}

          <p className="text-default-500 mt-1 text-xs">
            {options.hasMultipleCustomerAppointments ? null : options.wouldIssueCredit ? (
              <Trans>
                Glede na nastavitve je stranka upravičena do nadomestnega
                termina.
              </Trans>
            ) : (
              <Trans>
                Glede na nastavitve stranka ni upravičena do nadomestnega
                termina.
              </Trans>
            )}
          </p>
        </div>
      )}

      {options.hasCreditBackedCustomerAppointments && (
        <div>
          <p className="text-default-700 text-xs">
            {options.hasMultipleCustomerAppointments ? (
              <Trans>
                Vsaj ena stranka je termin rezervirala z dodatnim obiskom.
              </Trans>
            ) : (
              <Trans>Stranka je termin rezervirala z dodatnim obiskom.</Trans>
            )}
          </p>

          <div className="mt-2 flex flex-col gap-1">
            <LimeCheckbox
              size="sm"
              {...form.getInputProps("reissueCredit", { type: "checkbox" })}
            >
              <Trans>Vrni dodatni obisk</Trans>
            </LimeCheckbox>
            {form.values.reissueCredit && (
              <div className="ml-6 flex items-center gap-1.5">
                <LimeCheckbox
                  size="sm"
                  {...form.getInputProps("extendCredit", { type: "checkbox" })}
                >
                  <Trans>Podaljšaj veljavnost dodatnega termina</Trans>
                </LimeCheckbox>
                <LimeInfoTooltip
                  ariaLabel="Informacije o podaljšanju veljavnosti"
                  content={
                    <div className="max-w-72 text-xs leading-5">
                      <p>
                        <Trans>Nova veljavnost se izračuna takole:</Trans>
                      </p>
                      <ul className="mt-1 list-disc pl-4">
                        <li>
                          <Trans>
                            datumu termina (ali datumu odpovedi, če je termin že
                            mimo) se prišteje število dni iz nastavitev
                            nadomestnih terminov za ta paket, veljavnost pa
                            poteče ob koncu tega dne;
                          </Trans>
                        </li>
                        <li>
                          <Trans>
                            če je v nastavitvah vklopljeno »Vsaj do konca
                            obračunskega obdobja«, velja daljše od obeh - to
                            število dni ali konec trenutnega obračunskega
                            obdobja paketa;
                          </Trans>
                        </li>
                        <li>
                          <Trans>
                            pri paketih brez samodejnega podaljševanja
                            veljavnost ne seže čez konec paketa;
                          </Trans>
                        </li>
                        <li>
                          <Trans>
                            če dodatni obisk že velja dlje, obdrži obstoječi
                            datum - veljavnost se nikoli ne skrajša.
                          </Trans>
                        </li>
                      </ul>
                    </div>
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
